// ============================================================================
//  FiscalYearsPage.tsx — NexusCorp ERP
//  Gestion des exercices fiscaux SYSCOHADA
//  Cycle : OPEN → SOFT_CLOSED → HARD_CLOSED (irréversible)
//          Réouverture possible uniquement de SOFT_CLOSED → OPEN
// ============================================================================

import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Lock, Unlock, RefreshCw, CheckCircle,
  AlertTriangle, Calendar, ChevronRight,
} from 'lucide-react'
import { accountingApi } from '@/modules/accounting/api/accounting.api'
import { useNotificationStore } from '@/stores/notification.store'
import { PageHeader } from '@/components/layout/PageHeader'

// ─── Config statuts ───────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
  OPEN:        { label: 'Ouvert',        color: 'bg-[var(--green-bg)] text-[var(--green-text)] border-[rgba(16,185,129,0.25)]', dot: 'bg-[var(--green-text)]' },
  SOFT_CLOSED: { label: 'Pré-clôturé',  color: 'bg-[var(--amber-bg)] text-[var(--amber-text)] border-[rgba(245,158,11,0.25)]', dot: 'bg-[var(--amber-text)]' },
  HARD_CLOSED: { label: 'Clôturé',      color: 'bg-[var(--surface-2)] text-[var(--tx3)] border-[var(--bd)]', dot: 'bg-[var(--tx4)]' },
}

const fmt = (d: string) => d ? new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

// ─── Modal action ─────────────────────────────────────────────────────────────
function ActionModal({
  title, description, warning, placeholder, minLength = 10,
  onConfirm, onClose, isPending, confirmLabel, confirmColor = 'var(--blue)',
}: {
  title: string; description: string; warning?: string
  placeholder: string; minLength?: number
  onConfirm: (reason: string) => void; onClose: () => void
  isPending: boolean; confirmLabel: string; confirmColor?: string
}) {
  const [reason, setReason] = useState('')
  return (
    <div className="fixed inset-0 bg-[rgba(22,12,38,0.5)] backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white border border-[var(--bd)] rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-[var(--tx)] mb-2">{title}</h3>
        <p className="text-sm text-[var(--tx3)] mb-3">{description}</p>
        {warning && (
          <div className="flex items-start gap-2 p-3 bg-[var(--red-bg)] border border-[rgba(220,38,38,0.2)] rounded-lg mb-4">
            <AlertTriangle className="w-4 h-4 text-[var(--red-text)] shrink-0 mt-0.5" />
            <p className="text-xs text-[var(--red-text)]">{warning}</p>
          </div>
        )}
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] resize-none focus:outline-none focus:border-[var(--blue)] mb-4"
        />
        <p className="text-xs text-[var(--tx4)] mb-4">{reason.length}/{minLength} caractères minimum</p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm transition-colors hover:bg-[var(--surface-3)]">
            Annuler
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={reason.length < minLength || isPending}
            style={{ background: confirmColor }}
            className="flex-1 py-2 text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-opacity"
          >
            {isPending ? 'En cours…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal création exercice ──────────────────────────────────────────────────
function CreateFiscalYearModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const currentYear = new Date().getFullYear()
  const [form, setForm] = useState({
    year:      currentYear,
    label:     `Exercice ${currentYear}`,
    startDate: `${currentYear}-01-01`,
    endDate:   `${currentYear}-12-31`,
  })

  const { mutate, isPending } = useMutation({
    mutationFn: () => accountingApi.createFiscalYear(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fiscal-years'] })
      addNotification({ type: 'success', title: 'Exercice créé', message: form.label })
      onClose()
    },
    onError: (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message }),
  })

  return (
    <div className="fixed inset-0 bg-[rgba(22,12,38,0.5)] backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white border border-[var(--bd)] rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-[var(--tx)]">Nouvel exercice fiscal</h3>
          <button onClick={onClose} className="text-[var(--tx3)] hover:text-[var(--tx)]">✕</button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Année</label>
            <input
              type="number"
              value={form.year}
              onChange={e => setForm(f => ({ ...f, year: +e.target.value, label: `Exercice ${e.target.value}` }))}
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Libellé</label>
            <input
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Début</label>
              <input
                type="date"
                value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Fin</label>
              <input
                type="date"
                value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
              />
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2 bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm hover:bg-[var(--surface-3)] transition-colors">
            Annuler
          </button>
          <button
            onClick={() => mutate()}
            disabled={!form.label || !form.startDate || !form.endDate || isPending}
            className="flex-1 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
          >
            {isPending ? 'Création…' : 'Créer l\'exercice'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function FiscalYearsPage() {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [showCreate, setShowCreate] = useState(false)
  const [modal, setModal] = useState<{
    type: 'soft-close' | 'hard-close' | 'reopen'
    fyId: string
    fyLabel: string
  } | null>(null)

  const { data: fiscalYears = [], isLoading } = useQuery({
    queryKey: ['fiscal-years'],
    queryFn:  accountingApi.getFiscalYears,
  })

  const mutOpts = (successMsg: string) => ({
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fiscal-years'] })
      setModal(null)
      addNotification({ type: 'success', title: 'Exercice mis à jour', message: successMsg })
    },
    onError: (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message }),
  })

  const { mutate: softClose, isPending: isSoftClosing } = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      accountingApi.softCloseFiscalYear(id, reason),
    ...mutOpts('Exercice pré-clôturé.'),
  })

  const { mutate: hardClose, isPending: isHardClosing } = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      accountingApi.hardCloseFiscalYear(id, reason),
    ...mutOpts('Exercice clôturé définitivement.'),
  })

  const { mutate: reopen, isPending: isReopening } = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      accountingApi.reopenFiscalYear(id, reason),
    ...mutOpts('Exercice réouvert.'),
  })

  const handleAction = (reason: string) => {
    if (!modal) return
    if (modal.type === 'soft-close') softClose({ id: modal.fyId, reason })
    if (modal.type === 'hard-close') hardClose({ id: modal.fyId, reason })
    if (modal.type === 'reopen')     reopen({ id: modal.fyId, reason })
  }

  const isPending = isSoftClosing || isHardClosing || isReopening

  const MODAL_CONFIG = {
    'soft-close': {
      title: 'Pré-clôturer l\'exercice',
      description: 'La pré-clôture (Soft Close) restreint la saisie mais reste réversible. Utilisée pour préparer les états de fin d\'exercice.',
      warning: undefined,
      placeholder: 'Motif de la pré-clôture (ex: préparation clôture annuelle)…',
      confirmLabel: 'Pré-clôturer',
      confirmColor: 'var(--amber-text)',
    },
    'hard-close': {
      title: 'Clôturer définitivement l\'exercice',
      description: 'La clôture définitive (Hard Close) est irréversible. Aucune écriture ne pourra être saisie sur cet exercice.',
      warning: 'ATTENTION — Cette action est irréversible. Assurez-vous que tous les états financiers ont été approuvés.',
      placeholder: 'Motif de clôture définitive (minimum 10 caractères)…',
      confirmLabel: 'Clôturer définitivement',
      confirmColor: '#DC2626',
    },
    'reopen': {
      title: 'Réouvrir l\'exercice',
      description: 'Réouvre un exercice pré-clôturé pour permettre de nouvelles saisies. Nécessite une justification.',
      warning: undefined,
      placeholder: 'Motif de réouverture (ex: correction d\'écriture)…',
      confirmLabel: 'Réouvrir',
      confirmColor: 'var(--blue)',
    },
  }

  return (
    <div className="space-y-5 nx-fade-up">
      <PageHeader
        title="Exercices fiscaux"
        description="Gestion du cycle de vie des exercices SYSCOHADA"
        breadcrumb={[{ label: 'Comptabilité' }, { label: 'Exercices' }]}
        actions={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvel exercice
          </button>
        }
      />

      {/* Guide cycle de vie */}
      <div className="flex items-center gap-2 text-xs text-[var(--tx3)] bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-4 py-3">
        <span className="px-2 py-0.5 bg-[var(--green-bg)] text-[var(--green-text)] rounded-md font-medium">OPEN</span>
        <ChevronRight className="w-3 h-3" />
        <span className="px-2 py-0.5 bg-[var(--amber-bg)] text-[var(--amber-text)] rounded-md font-medium">SOFT_CLOSED</span>
        <ChevronRight className="w-3 h-3" />
        <span className="px-2 py-0.5 bg-[var(--surface-3)] text-[var(--tx3)] rounded-md font-medium">HARD_CLOSED</span>
        <span className="ml-2">— Seul le passage OPEN ↔ SOFT_CLOSED est réversible</span>
      </div>

      {/* Liste exercices */}
      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-[var(--surface-2)] rounded-xl animate-pulse" />)}
        </div>
      ) : (fiscalYears as any[]).length === 0 ? (
        <div className="text-center py-16 text-[var(--tx3)]">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">Aucun exercice fiscal créé</p>
          <p className="text-xs mt-1">Créez le premier exercice pour commencer à saisir des écritures</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-4 px-4 py-2 bg-[var(--blue)] text-white rounded-lg text-sm font-semibold"
          >
            Créer l'exercice {new Date().getFullYear()}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {(fiscalYears as any[]).map((fy: any) => {
            const cfg = STATUS_CONFIG[fy.status] ?? STATUS_CONFIG['HARD_CLOSED']
            return (
              <div key={fy.id} className="nx-card p-5 hover:shadow-sm transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[var(--surface-2)] border border-[var(--bd)] flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-[var(--tx3)]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[var(--tx)]">{fy.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-md border font-medium ${cfg.color}`}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--tx3)] mt-0.5">
                        {fmt(fy.startDate)} → {fmt(fy.endDate)}
                        {fy.softClosedAt && ` · Pré-clôturé le ${fmt(fy.softClosedAt)}`}
                        {fy.hardClosedAt && ` · Clôturé le ${fmt(fy.hardClosedAt)}`}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {fy.status === 'OPEN' && (
                      <button
                        onClick={() => setModal({ type: 'soft-close', fyId: fy.id, fyLabel: fy.label })}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--amber-bg)] text-[var(--amber-text)] border border-[rgba(245,158,11,0.25)] rounded-lg text-xs font-medium hover:opacity-80 transition-opacity"
                      >
                        <Lock className="w-3.5 h-3.5" />
                        Pré-clôturer
                      </button>
                    )}
                    {fy.status === 'SOFT_CLOSED' && (
                      <>
                        <button
                          onClick={() => setModal({ type: 'reopen', fyId: fy.id, fyLabel: fy.label })}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--blue-bg)] text-[var(--blue-d)] border border-[rgba(245,124,0,0.25)] rounded-lg text-xs font-medium hover:opacity-80 transition-opacity"
                        >
                          <Unlock className="w-3.5 h-3.5" />
                          Réouvrir
                        </button>
                        <button
                          onClick={() => setModal({ type: 'hard-close', fyId: fy.id, fyLabel: fy.label })}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--red-bg)] text-[var(--red-text)] border border-[rgba(220,38,38,0.2)] rounded-lg text-xs font-medium hover:opacity-80 transition-opacity"
                        >
                          <Lock className="w-3.5 h-3.5" />
                          Clôturer
                        </button>
                      </>
                    )}
                    {fy.status === 'HARD_CLOSED' && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 text-[var(--tx4)] text-xs">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Clôturé définitivement
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modals */}
      {showCreate && <CreateFiscalYearModal onClose={() => setShowCreate(false)} />}
      {modal && (
        <ActionModal
          {...MODAL_CONFIG[modal.type]}
          onConfirm={handleAction}
          onClose={() => setModal(null)}
          isPending={isPending}
        />
      )}
    </div>
  )
}
